// Angular
import { LOCALE_ID, ModuleWithProviders } from '@angular/core';
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgModule } from "@angular/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { DatePipe, LocationStrategy, registerLocaleData } from "@angular/common";
import { HashLocationStrategy } from "@angular/common";
import { ErrorHandler } from "@angular/core";
import { IconModule } from '@progress/kendo-angular-icons';

// Application
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

// Components
import { NavMenuComponent } from "app/_components/menu/navmenu/navmenu.component";
import { SideMenuComponent } from "app/_components/menu/sidemenu/sidemenu.component";
import { HeaderMenuComponent } from "app/_components/menu/headermenu/headermenu.component";
import { BreadcrumbMenuComponent } from "app/_components/menu/breadcrumbmenu/breadcrumbmenu.component";
// Layouts
import { FullLayoutComponent } from "app/_layouts/full-layout.component";
import { SimpleLayoutComponent } from "app/_layouts/simple-layout.component";

import { SignInComponent } from "app/_authentication/signin.component";
import { WindowRef } from "app/_shared/window.ref";
import { ThemeModule } from "app/_components/theme/theme.module";

// Helpers
import { JwtInterceptor } from "app/_helpers/jwt.interceptor";
import { HttpErrorInterceptor } from "app/_helpers/httperror.interceptor";
import { ImageInterceptor } from "app/_helpers/image.interceptor";
import { fakeBackendProvider } from "app/_helpers/fakebackend.interceptor";
import { delayedRetry } from "app/_helpers/delayedretry.helper";


// Services
import { AuthService } from "app/_services";
import { AuthenticationService } from "app/_services/authentication.service";
import { SharedService } from "app/_services";

// Ngxs
import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { IntakeState } from 'app/_state/intake.state';

// Shared
import { SharedModule } from "app/_shared/shared.module";

// Security
import { AuthGuard } from "app/_authentication/auth.guard";

// Notification
import { LoadingBarHttpClientModule } from "@ngx-loading-bar/http-client";
import { NotificationBarModule } from "ngx-notification-bar";

// error
//import { GlobalErrorHandler } from "app/app.global-error-handler";

// ngx-translate
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { HttpClientModule, HttpClient } from "@angular/common/http";

// 3rd Party
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { McBreadcrumbsModule, McBreadcrumbsConfig } from "ngx-breadcrumbs";
import { NgForageConfig, Driver } from "ngforage";
import { ToastrModule } from "ngx-toastr";
import { ManagementModule } from "./management/management.module";
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { DossierModule } from "./dossier/dossier.module";
import { CalamityModule } from "./calamity/calamity.module";

import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import localeNl from '@angular/common/locales/nl';
import { AgmCoreModule } from '@agm/core';
import { environment } from 'environments/environment';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { CorrespondanceDetailsComponent } from './intake/corresondance/correspondance-details/correspondance-details.component';
import { DossierActionsComponent } from './intake/corresondance/actions/dossier-actions.component';
import { GenesysCallsComponent } from './_layouts/genesys-calls/genesys-calls.component';
import { DossierGenesysFiltredComponent } from './dossier/dossier-genesys-filtred/dossier-genesys-filtred.component';
import { DraftDossierModule } from './draft-dossier/draft-dossier.module';

registerLocaleData(localeNl);

declare var $: any;
console.log(`jQuery version: ${$.fn.jquery}`);

@NgModule({
  declarations: [
    AppComponent,
    SignInComponent,
    FullLayoutComponent,
    SimpleLayoutComponent,
    NavMenuComponent,
    SideMenuComponent,
    HeaderMenuComponent,
    BreadcrumbMenuComponent,
    CorrespondanceDetailsComponent,
    DossierActionsComponent,
    GenesysCallsComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    ManagementModule,
    DossierModule,
    DraftDossierModule,
    PDFExportModule,
    CalamityModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
    }),
    NgxsModule.forRoot([IntakeState]),
    NgxsStoragePluginModule.forRoot({
      key: [IntakeState],
      storage: StorageOption.SessionStorage
    }),
    NotificationBarModule,
    LoadingBarHttpClientModule,
    BsDropdownModule.forRoot(),
    McBreadcrumbsModule.forRoot(),
    ToastrModule.forRoot({
      preventDuplicates: true
    }),
    ThemeModule,
    TreeViewModule,
    IconModule,
    NotificationModule,
    NgxsModule.forRoot([IntakeState]),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsLoggerPluginModule.forRoot(),

    AgmCoreModule.forRoot({
      apiKey: environment.Keys.Google,
      libraries: ["places"]
    }),
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    { provide: LOCALE_ID, useValue: 'nl-DE' },
    // {
    //   provide: ErrorHandler,
    //   useClass: GlobalErrorHandler,
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ImageInterceptor,
      multi: true,
    },
    AuthService,
    AuthenticationService,
    AuthGuard,
    SharedService,
    WindowRef,
    DatePipe,
    //fakeBackendProvider
  ],
  entryComponents: [
    CorrespondanceDetailsComponent,
    DossierActionsComponent,
    DossierGenesysFiltredComponent,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    breadcrumbsConfig: McBreadcrumbsConfig,
    ngfConfig: NgForageConfig,
  ) {
    ngfConfig.configure({
      name: "CED",
      driver: [Driver.LOCAL_STORAGE, Driver.INDEXED_DB],
    });
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}
